import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import useAxiosInstance from "../../../../../Utilities/Hooks/useAxiosInstance";

import {
  BUNDLE_OFFERS_API,
  PRODUCTS_API,
  CATAGORY_API,
  BRANDS_API,
  PRODUCT_COLLECTIONS_API,
} from "../../../../../Utilities/APIs/APIs";

import Form from "../../../../Partials/Layouts/Forms/Form";
import ShortTextInput from "../../../../Partials/Layouts/Forms/FormInputs/ShortTextInput/ShortTextInput";
import FormSubmitButton from "../../../../Partials/Layouts/Forms/FormSubmitButton/FormSubmitButton";
import DateInput from "../../../../Partials/Layouts/Forms/FormInputs/DateInput/DateInput";
import SelectInput from "../../../../Partials/Layouts/Forms/FormInputs/SelectInput/SelectInput";
import SelectOption from "../../../../Partials/Layouts/Forms/FormInputs/SelectInput/SelectOption/SelectOption";

import BundleTargetPicker from "./BundleTargetPicker";
import "./bundleOffer.css";

function UpdateBundleOffer({
  targetID,
  triggerFetch,
  setShowUpdateForm,
}) {
  const axiosInstance = useAxiosInstance();

  const [name, setName] = useState("");
  const [appliesTo, setAppliesTo] =useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] =useState([]);
  const [brands, setBrands] = useState([]);
  const [collections, setCollections] =useState([]);
  const [productGroups, setProductGroups] =useState([]);
  const [requiredQuantity, setRequiredQuantity] =useState(2);
  const [discountValue, setDiscountValue] =useState(50);
  const [customerLabel, setCustomerLabel] = useState("");
  const [offerLink, setOfferLink] = useState("");
  const [startDate, setStartDate] =useState("");
  const [endDate, setEndDate] =useState("");
  const [isActive, setIsActive] =useState(false);
  const [productOptions, setProductOptions] =useState([]);
  const [categoryOptions, setCategoryOptions] =useState([]);
  const [brandOptions, setBrandOptions] =useState([]);
  const [collectionOptions,setCollectionOptions,] = useState([]);
  const [productGroupOptions,setProductGroupOptions,] = useState([]);

  const [error, setError] = useState("");
  const [submitting, setSubmitting] =useState(false);
  const [discountType, setDiscountType] =useState("amount");

  useEffect(() => {
    async function loadData() {
      try {
        const [
          offerRes,
          productRes,
          categoryRes,
          brandRes,
          collectionRes,
        ] = await Promise.all([
          axiosInstance.get(BUNDLE_OFFERS_API + targetID),
          axiosInstance.get(`${PRODUCTS_API}?page=1&limit=1000`),
          axiosInstance.get(`${CATAGORY_API}?page=1&limit=1000`),
          axiosInstance.get(`${BRANDS_API}?page=1&limit=1000`),
          axiosInstance.get(`${PRODUCT_COLLECTIONS_API}?page=1&limit=1000`),
        ]);

        const data = offerRes?.data;

        setName(data?.name || "");
        setAppliesTo(
          data?.appliesTo || "products"
        );

        setProducts(
          (data?.products || []).map(
            (item) =>
              typeof item === "string"
                ? item
                : item._id
          )
        );

        setCategories(
          (data?.categories || []).map(
            (item) =>
              typeof item === "string"
                ? item
                : item._id
          )
        );

        setBrands(
          (data?.brands || []).map(
            (item) =>
              typeof item === "string"
                ? item
                : item._id
          )
        );

        setCollections(
          (data?.collections || []).map(
            (item) =>
              typeof item === "string"
                ? item
                : item._id
          )
        );

        setProductGroups(data?.productGroups || []);
        setRequiredQuantity(data?.requiredQuantity || 2);
        setDiscountValue(data?.discountValue || 0);
        setCustomerLabel(data?.customerLabel || "");
        setOfferLink(data?.offerLink || "");
        setIsActive(Boolean(data?.isActive));

        if (data?.startDate) {
          setStartDate(
            new Date(data.startDate)
              .toISOString()
              .slice(0, 10)
          );
        }

        if (data?.endDate) {
          setEndDate(
            new Date(data.endDate)
              .toISOString()
              .slice(0, 10)
          );
        }
        setDiscountType(
        data?.discountType || "amount"
        );

        const productData =
          productRes?.data?.products ||
          productRes?.data ||
          [];

        setProductOptions(
          productData.map((product) => ({
            value: product._id,
            label:
              product.name ||
              product.squId,
          }))
        );

        const groups = [
          ...new Set(
            productData
              .map((p) => p.productGroup)
              .filter(Boolean)
          ),
        ];

        setProductGroupOptions(
          groups.map((group) => ({
            value: group,
            label: group,
          }))
        );

        const categoryData =
          categoryRes?.data?.categories ||
          categoryRes?.data ||
          [];

        setCategoryOptions(
          categoryData.map((category) => ({
            value: category._id,
            label:
              category.categoryName ||
              category.name,
          }))
        );

        const brandData =
          brandRes?.data?.brands ||
          brandRes?.data ||
          [];

        setBrandOptions(
          brandData.map((brand) => ({
            value: brand._id,
            label: brand.name,
          }))
        );

        const collectionData =
          collectionRes?.data
            ?.productCollections ||
          collectionRes?.data?.collections ||
          collectionRes?.data ||
          [];

        setCollectionOptions(
          collectionData.map((collection) => ({
            value: collection._id,
            label: collection.name,
          }))
        );
      } catch (error) {
        console.error(
          "Failed loading bundle offer:",
          error
        );
      }
    }

    if (targetID) {
      loadData();
    }
  }, [targetID, axiosInstance]);

  const preview = useMemo(() => {
    const qty =
      Number(requiredQuantity) || 0;

    const discount =
      Number(discountValue) || 0;

    if (!qty || !discount) return null;

    return {
      qty,
      discount,
    };
  }, [requiredQuantity, discountValue]);

  async function handleSubmit(e) {
    e.preventDefault();

    setError("");

    if (!name.trim()) {
      setError(
        "Please enter an offer name."
      );
      return;
    }

    if (
      Number(requiredQuantity) < 1
    ) {
      setError(
        "Required quantity must be at least 1."
      );
      return;
    }

    if (Number(discountValue) <= 0) {
      setError(
        "Discount amount must be greater than 0."
      );
      return;
    }

    const itemData = {
    name: name.trim(),
    appliesTo,
    products,
    categories,
    brands,
    collections,
    productGroups,
    requiredQuantity: Number(requiredQuantity),
    discountType,
    discountValue: Number(discountValue),
    customerLabel,
    offerLink,
    startDate,
    endDate,
    isActive,
    };

    try {
      setSubmitting(true);

      const response =
        await axiosInstance.patch(
          BUNDLE_OFFERS_API + targetID,
          itemData
        );

      if (response) {
        triggerFetch();
        setShowUpdateForm(false);
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          "Failed to update bundle offer."
      );
    } finally {
      setSubmitting(false);
    }
  }

  const pickerProps = {
    products: {
      label: "Eligible Products",
      options: productOptions,
      selected: products,
      setSelected: setProducts,
    },

    category: {
      label: "Eligible Categories",
      options: categoryOptions,
      selected: categories,
      setSelected: setCategories,
    },

    brand: {
      label: "Eligible Brands",
      options: brandOptions,
      selected: brands,
      setSelected: setBrands,
    },

    collection: {
      label: "Eligible Collections",
      options: collectionOptions,
      selected: collections,
      setSelected: setCollections,
    },

    productGroup: {
      label: "Eligible Product Groups",
      options: productGroupOptions,
      selected: productGroups,
      setSelected: setProductGroups,
    },
  };

return (
  <div className="bundle-offer-form">
    <Form onSubmit={handleSubmit}>
      <div>

        <ShortTextInput
          label="Offer Name"
          value={name}
          placeholder="Offer Name"
          setState={setName}
        />

        <div className="bundle-help-text">
          Internal name used to identify this bundle offer.
        </div>


        <div style={{ marginTop: "18px" }}>
          <SelectInput
            label="Applies To"
            value={appliesTo}
            setState={setAppliesTo}
          >
            <SelectOption
              optionValue="products"
              optionText="Selected Products"
            />

            <SelectOption
              optionValue="category"
              optionText="Selected Categories"
            />

            <SelectOption
              optionValue="brand"
              optionText="Selected Brands"
            />

            <SelectOption
              optionValue="collection"
              optionText="Selected Collections"
            />

            <SelectOption
              optionValue="productGroup"
              optionText="Product Groups"
            />

            <SelectOption
              optionValue="all"
              optionText="All Products"
            />
          </SelectInput>
        </div>

        {pickerProps[appliesTo] && (
          <BundleTargetPicker
            {...pickerProps[appliesTo]}
          />
        )}

        {appliesTo === "all" && (
          <div className="bundle-info-box">
            Every non-discounted product will count toward
            this bundle offer.
          </div>
        )}

        <ShortTextInput
          label="Buy Quantity"
          value={requiredQuantity}
          placeholder="2"
          setState={setRequiredQuantity}
        />

        <div className="bundle-help-text">
          Number of eligible items needed for one bundle.
        </div>

        <SelectInput
            label="Discount Type"
            value={discountType}
            setState={setDiscountType}
        >
        <SelectOption
            optionValue="amount"
            optionText="Fixed Amount (৳)"
        />

        <SelectOption
            optionValue="percentage"
            optionText="Percentage (%)"
        />
        </SelectInput>

        <ShortTextInput
        label={
            discountType === "percentage"
            ? "Discount Percentage (%)"
            : "Discount Amount (৳)"
        }
        value={discountValue}
        placeholder={
            discountType === "percentage"
            ? "e.g. 5"
            : "e.g. 50"
        }
        setState={setDiscountValue}
        />

        <div className="bundle-help-text">
          Discount automatically repeats for every complete
          bundle.
        </div>


        {preview && (
          <div className="bundle-preview">

            <div className="bundle-preview-title">
              Offer Preview
            </div>

            <div className="bundle-preview-primary">
              Buy {preview.qty} → Get ৳
              {preview.discount} Off
            </div>

            <div className="bundle-preview-secondary">
              Buy {preview.qty * 2} → ৳
              {preview.discount * 2} off
              <br />

              Buy {preview.qty * 3} → ৳
              {preview.discount * 3} off
            </div>

            <div className="bundle-preview-note">
              Already discounted products are automatically
              excluded.
            </div>
          </div>
        )}

        <ShortTextInput
          label="Label for Customers (Optional)"
          value={customerLabel}
          setState={setCustomerLabel}
        />

         <ShortTextInput
          label="Link"
          value={offerLink}
          setState={setOfferLink}
        />

        <DateInput
          label="Start Date"
          value={startDate}
          setState={setStartDate}
        />

        <div style={{ marginTop: "18px" }}>
          <DateInput
            label="End Date"
            value={endDate}
            setState={setEndDate}
          />
        </div>

        <label className="bundle-active-row">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) =>
              setIsActive(e.target.checked)
            }
          />
          <span>
            Active
          </span>
        </label>


        {error && (
          <p className="bundle-error">
            {error}
          </p>
        )}

      </div>

      <FormSubmitButton
        text={
          submitting
            ? "Updating..."
            : "Update Bundle Offer"
        }
      />
    </Form>
  </div>
);
}

export default UpdateBundleOffer;